import React, { useEffect } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import "./Breadcrumbs.css";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbDivider,
  BreadcrumbButton,
} from "@fluentui/react-breadcrumb-preview";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  displayRecordCount,
  getItemDisplayValue,
  ItemDisplayKeys,
} from "../../utils";

const breadcrumbNameMap: any = {
  clients: "Clients",
  "action-list": "Action List",
  utilities: "Utilities",
  report: "Reports",
  "system-manager": "System Manager",
  commission: "Commission",
  distribution: "Distribution",
  "other-fees-charges": "Other Fees/Charges",
  receipting: "Receipting",
  "manage-user": "Manage User",
  "master-settings": "Master Settings",
  "commission-type": "Commission Type",
  "commission-rule": "Commission Rule",
  subscription: "Subscription",
  "company-detail": "Company Details",
  "income-category": "Income Categories",
  "expense-category": "Expense Categories",
  "attitude-risk-categories": "Attitude-to-Risk Categories",
  "attitude-risk-ratings": "Attitude-to-Risk Ratings",
  objectives: "Objectives",
  providers: "Providers",
  "user-defined": "User Defined",
  "case-action": "Case Action",
  "client-action": "Client Action",
  "email-template": "Email ",
  "letter-template": "Letter ",
  "contract-enquiry": "Contract Enquiry",
  integrations: "Integrations",
  "company-details": "Company Details",
  customisations: "Customisations",
  templates: "Templates",
  template: "Templates",
  assets: "Assets",
  asset: "Asset",
  view: "View",
  edit: "Edit",
  add: "Add",
  review: "Review",
  "name-and-address": "Name And Address",
  contact: "Contact",
  nationality: "Nationality",
  personal: "Personal",
  "health-note": "Health Note",
  vulnerability: "Vulnerability",
  employment: "Employment",
  "attitude-to-risk": "Attitude to Risk",
  identity: "Identity",
  administration: "Administration",
  compliance: "Compliance",
  "service-type": "Service Type",
  "client-summary": "Client Summary",
  "case-summary": "Case Summary",
  "contact-history": "Contact History",
  partner: "Partner",
  payments: "Payments",
  payment: "Payment",
  withdrawals: "Withdrawals",
  actions: "Actions",
  valuations: "Valuations",
  funds: "Funds",
  fund: "Fund",
  withdrawal: "Withdrawal",
  action: "Action",
  valuation: "Valuation",
  liabilities: "Liabilities",
  liability: "Liability",
  policies: "Policies",
  policy: "Policy",
  dependant: "Dependant",
  income: "Income",
  outgoings: "Outgoings",
  "address-book": "Address Book",
  "fact-find-notes": "Fact Find Notes",
  "fees-charges": "Fees/Charges",
  notes: "Notes",
  "time-allocation": "Time Allocation",
  documents: "Documents",
  contacts: "Contacts",
  email: "Email",
  letter: "Letter",
  assyst_portal: "Assyst Portal",
  client_portal: "Client Portal",
  profile: "Profile",
  account: "Account",
  emailSettings: "Email Settings",
  run_logs: "Run Logs",
};

const Breadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeClient: any = useSelector(
    (state: RootState) => state.activeClient
  );
  const assets: any = useSelector((state: RootState) => state.assets);
  const liabilities: any = useSelector((state: RootState) => state.liabilities);
  const policies: any = useSelector((state: RootState) => state.policies);
  const vulnerabilityList: any =
    useSelector((state: RootState) => state.vulnerability?.listItems) || [];
  const partnerVulnerabilityList: any =
    useSelector((state: RootState) => state.partnerVulnerability?.listItems) ||
    [];
  const clientContacts = useSelector(
    (state: RootState) => state.clientContacts
  );
  const pathnames = location.pathname.split("/").filter((x) => x);
  const {
    clientId,
    caseId,
    subItemId,
    subSubItemId,
    action,
    selectedItemId,
    distributionId,
    itemId,
  } = useParams();

  const handleNavigationLink = (link: any) => {
    navigate(link);
  };
  const currentPath = location.pathname.replace(/^\/+/, "");
  const hiddenPaths = ["/clients", "/", "/action-list", "/report"];

  if (hiddenPaths.includes(location.pathname)) {
    return <div></div>;
  }
  return (
    <Breadcrumb
      aria-label="Breadcrumb"
      style={{
        padding: currentPath.startsWith("profile") ? "0 20px" : 0,
      }}
    >
      <BreadcrumbItem>
        <BreadcrumbButton onClick={() => handleNavigationLink("/")}>
          Home
        </BreadcrumbButton>
      </BreadcrumbItem>
      {currentPath == "" && (
        <>
          <BreadcrumbDivider />
          <BreadcrumbItem>
            <BreadcrumbButton onClick={() => handleNavigationLink("/")} current>
              Clients
            </BreadcrumbButton>
          </BreadcrumbItem>
        </>
      )}

      {pathnames.map((value, index) => {
        const to = `/${pathnames.slice(0, index + 1).join("/")}`;
        const isLast = index === pathnames.length - 1;
        let displayValue = value;
        const pathSections = location.pathname.split("/");
        const caseType = ["assets", "liabilities", "policies"].find((type) =>
          pathSections.includes(type)
        );
        if (value === clientId) {
          const firstName = activeClient?.basicDetails?.first_names;
          const lastName = activeClient?.basicDetails?.last_name;

          if (lastName) {
            displayValue = firstName ? `${lastName}, ${firstName}` : lastName;
          } else {
            displayValue = "";
          }
        } else if (value === caseId) {
          const idKey =
            caseType === "policies"
              ? "policy_id"
              : caseType === "liabilities"
              ? "liability_id"
              : "asset_id";

          const itemsArray =
            caseType === "policies"
              ? policies?.listItems
              : caseType === "liabilities"
              ? liabilities?.listItems
              : assets?.listItems;
          const activeItem =
            caseType === "policies"
              ? policies?.activeItem
              : caseType === "liabilities"
              ? liabilities?.activeItem
              : assets?.activeItem;
          const activeItemIndex = itemsArray?.findIndex(
            (item: any) => item[idKey] === activeItem?.[idKey]
          );

          if (displayRecordCount().includes(caseType)) {
            displayValue = `Record ${parseInt(activeItemIndex) + 1}`;
          } else {
            displayValue = getItemDisplayValue(
              activeItem,
              caseType ? caseType : "",
              ItemDisplayKeys
            );
          }
        } else if (value === subItemId || value === selectedItemId) {
          // ---- VULNERABILITY ----
          const activeVulnerability = vulnerabilityList.find(
            (item: any) => item.vulnerability_id === value
          );
          const activePartnerVulnerability = partnerVulnerabilityList.find(
            (item: any) => item.vulnerability_id === value
          );

          const vulnerabilityIndex = activeVulnerability
            ? vulnerabilityList.findIndex(
                (item: any) => item.vulnerability_id === value
              ) + 1
            : activePartnerVulnerability
            ? partnerVulnerabilityList.findIndex(
                (item: any) => item.vulnerability_id === value
              ) + 1
            : 0;

          // ---- CONTACTS (client-action, notes, time-allocation) ----
          const contactSections: any = {
            "client-action": {
              list: clientContacts?.clientActions?.listItems,
              idKey: "appointment_id",
            },
            notes: {
              list: clientContacts?.notes?.listItems,
              idKey: "note_id",
            },
            "time-allocation": {
              list: clientContacts?.timeAllocations?.listItems,
              idKey: "timeallocation_id",
            },
          };

          const sectionKey = location.state?.section || "";
          const activeContactSection = contactSections[sectionKey];

          let contactIndex = 0;

          if (activeContactSection?.list?.length > 0) {
            const activeContactItem = activeContactSection.list.find(
              (item: any) => item[activeContactSection.idKey] === value
            );
            if (activeContactItem) {
              contactIndex =
                activeContactSection.list.findIndex(
                  (item: any) => item[activeContactSection.idKey] === value
                ) + 1;
            }
          }

          // ---- DISPLAY VALUE LOGIC ----
          if (
            location.state?.index &&
            location.state.index !== undefined &&
            displayRecordCount().includes(location.state?.section || "")
          ) {
            displayValue = `Record ${location.state.index}`;
          } else if (vulnerabilityIndex > 0) {
            displayValue = `Record ${vulnerabilityIndex}`;
          } else if (contactIndex > 0) {
            displayValue = `Record ${contactIndex}`;
          } else {
            displayValue = getItemDisplayValue(
              location.state?.selectedItem,
              sectionKey,
              ItemDisplayKeys
            );
          }
        } else if (value === subSubItemId || value === distributionId) {
          displayValue = "Detail";
        } else if (value === itemId) {
          displayValue = "Record";
        } else if (value === action) {
          displayValue = action
            ? action.charAt(0).toUpperCase() + action.slice(1)
            : "View";
        } else {
          displayValue = breadcrumbNameMap[value] || value;
        }
        return (
          <React.Fragment key={to}>
            <BreadcrumbDivider />
            <BreadcrumbItem>
              {isLast ? (
                <BreadcrumbButton current>
                  {breadcrumbNameMap[displayValue] || displayValue}
                </BreadcrumbButton>
              ) : (
                <BreadcrumbButton
                  onClick={() =>
                    displayValue?.toLowerCase().includes("clients")
                      ? handleNavigationLink("/")
                      : handleNavigationLink(to)
                  }
                >
                  {breadcrumbNameMap[displayValue] || displayValue}
                </BreadcrumbButton>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        );
      })}
    </Breadcrumb>
  );
};

export default Breadcrumbs;
